import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  ClickAwayListener,
  Container,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { useNavigate, useSearchParams } from 'react-router-dom';
import bannerImg from '../images/pexels-gromakova-92378617-9146943.jpg';
import {
  darkPageBg,
  darkCardBg,
  accent,
  darkTextFieldSx,
  darkButtonSx,
  darkHeroSx,
} from './components/darkFormStyles';
import {
  searchByCity,
  searchByLocation,
  searchByPostcode,
} from '../middleware/registration_service/searchRequest';
import {
  getAddressFromCoordinates,
  getCoordinatesFromAddress,
  getCurrentPosition,
  getPostcodeAutocomplete,
  isCityName,
  isPostcode,
  PostcodeSuggestion,
} from '../utils/geocodingUtils';
import { SearchResult } from '../types/results';
import { companyName } from '../defaults/AppDefaults';
import StickyFooter from './components/StickyFooter';
import { filterOutTestAccounts } from '../utils/testAccountUtils';

// Session-persisted key so that navigating to a profile page and back
// ("Back to Results") restores the previous search instead of a blank form.
const SEARCH_STATE_KEY = 'findBarbersSearchState';

interface StoredSearchState {
  postcode: string;
  barbers: SearchResult[];
  searched: boolean;
}

const FindBarbers = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [postcode, setPostcode] = useState('');
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [barbers, setBarbers] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [locating, setLocating] = useState(false);
  const [suggestions, setSuggestions] = useState<PostcodeSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const autocompleteDebounceRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const latestQueryRef = useRef<string>('');

  const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPostcode(value);
    latestQueryRef.current = value; // Track the latest input

    if (autocompleteDebounceRef.current) {
      clearTimeout(autocompleteDebounceRef.current);
    }

    if (value.trim().length >= 2) {
      setLoading(true);
      autocompleteDebounceRef.current = setTimeout(async () => {
        const results = await getPostcodeAutocomplete(value);

        // Ensure the request corresponds to the latest input value before state updates
        if (latestQueryRef.current.trim() === value.trim()) {
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
          setLoading(false);
        }
      }, 300);
    } else {
      setLoading(false);
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (suggestion: PostcodeSuggestion) => {
    setPostcode(suggestion.postcode);
    setSuggestions([]);
    setShowSuggestions(false);
    handleSearch(suggestion.postcode);
  };

  const runSearch = async (searchFn: () => Promise<{ data: any }>) => {
    setError(null);
    setSearching(true);
    setSearched(false);
    try {
      const res = await searchFn();
      setBarbers(filterOutTestAccounts(res.data || []));
    } catch (err: any) {
      setBarbers([]);
      if (err?.response?.status === 404) {
        // No barbers found - treat as empty result
      } else {
        setError('Could not fetch barbers. Please try again later.');
      }
    } finally {
      setSearching(false);
      setSearched(true);
    }
  };

  // Routes the typed query to the best-matching search: a UK postcode,
  // a plain city/town name, or (via geocoding) a general location/address.
  const handleSearch = async (queryOverride?: string) => {
    const query = (queryOverride ?? postcode).trim();
    if (!query) {
      setError('Please enter a postcode, city or location.');
      return;
    }

    if (isPostcode(query)) {
      await runSearch(() => searchByPostcode(query));
      return;
    }

    if (isCityName(query)) {
      await runSearch(() => searchByCity(query));
      return;
    }

    const coordinates = await getCoordinatesFromAddress(query);
    if (!coordinates) {
      setError(
        "We couldn't find that location. Please try a valid postcode, city or address."
      );
      return;
    }
    await runSearch(() => searchByLocation(coordinates.lat, coordinates.lng));
  };

  // Uses the browser's geolocation to search by the user's current location.
  const handleUseMyLocation = async () => {
    setError(null);
    setLocating(true);
    try {
      const coordinates = await getCurrentPosition();
      const address = await getAddressFromCoordinates(coordinates);
      if (address) {
        setPostcode(address);
      }
      await runSearch(() => searchByLocation(coordinates.lat, coordinates.lng));
    } catch (err) {
      setError(
        'Could not determine your location. Please allow location access or enter a postcode/city instead.'
      );
    } finally {
      setLocating(false);
    }
  };

  // Auto-search when arriving from the landing page's "Find a Barber" section
  // with a postcode/city/location already provided in the URL
  // (?postcode=... or ?lat=...&lng=... for a browser-resolved location).
  useEffect(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    if (lat && lng) {
      const coordinates = { lat: parseFloat(lat), lng: parseFloat(lng) };
      getAddressFromCoordinates(coordinates).then((address) => {
        if (address) setPostcode(address);
      });
      runSearch(() => searchByLocation(coordinates.lat, coordinates.lng));
      return;
    }

    const initialPostcode = searchParams.get('postcode');
    if (initialPostcode) {
      setPostcode(initialPostcode);
      handleSearch(initialPostcode);
      return;
    }

    // No explicit search requested via URL - restore the last search
    // (e.g. when returning from a barber/shop profile page's back button).
    try {
      const stored = sessionStorage.getItem(SEARCH_STATE_KEY);
      if (stored) {
        const parsed: StoredSearchState = JSON.parse(stored);
        setPostcode(parsed.postcode || '');
        setBarbers(parsed.barbers || []);
        setSearched(parsed.searched || false);
      }
    } catch (err) {
      console.error('Failed to restore previous search results:', err);
    }
  }, []);

  // Persist the latest search results so they can be restored if the user
  // navigates to a profile page and clicks "Back to Results".
  useEffect(() => {
    if (!searched) return;
    try {
      const toStore: StoredSearchState = { postcode, barbers, searched };
      sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify(toStore));
    } catch (err) {
      console.error('Failed to persist search results:', err);
    }
  }, [postcode, barbers, searched]);

  return (
    <Box
      sx={{
        bgcolor: darkPageBg,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: '1 0 auto' }}>
        {/* Hero Banner */}
        <Box
          sx={{
            mt: '85px',
            py: 8,
            px: 2,
            ...darkHeroSx(bannerImg),
          }}
        >
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Find Barbers Near You
          </Typography>
          <Typography
            variant="h6"
            sx={{ maxWidth: 500, mx: 'auto', opacity: 0.85 }}
          >
            Enter your postcode or location to discover barbers in your area.
          </Typography>
        </Box>

        <Container maxWidth="md" sx={{ py: 5 }}>
          {/* Search Box */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              mb: 2,
              position: 'relative',
            }}
          >
            <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                  fullWidth
                  label="Enter Postcode, City or Location"
                  value={postcode}
                  onChange={handlePostcodeChange}
                  sx={darkTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Tooltip title="Use my current location">
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleUseMyLocation}
                              disabled={locating || searching}
                              aria-label="Use my current location"
                            >
                              {locating ? (
                                <CircularProgress
                                  size={18}
                                  sx={{ color: accent }}
                                />
                              ) : (
                                <MyLocationIcon
                                  sx={{ color: 'rgba(255,255,255,0.6)' }}
                                />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    endAdornment: loading ? (
                      <InputAdornment position="end">
                        <CircularProgress size={18} sx={{ color: accent }} />
                      </InputAdornment>
                    ) : null,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <Paper
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 10,
                      mt: 0.5,
                      bgcolor: darkCardBg,
                      border: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <List dense disablePadding>
                      {suggestions.map((suggestion) => (
                        <ListItemButton
                          key={suggestion.placeId}
                          onClick={() => handleSelectSuggestion(suggestion)}
                        >
                          <ListItemText
                            primary={suggestion.text}
                            sx={{ color: '#fff' }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>
            <Button
              variant="contained"
              onClick={() => handleSearch()}
              disabled={searching}
              startIcon={<SearchIcon />}
              sx={{ px: 4, minWidth: 140, ...darkButtonSx }}
            >
              {searching ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                'Search'
              )}
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Results */}
          {searched && (
            <>
              {barbers.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <ContentCutIcon
                    sx={{
                      fontSize: 56,
                      color: 'rgba(255,255,255,0.25)',
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ color: 'rgba(255,255,255,0.8)' }}
                  >
                    No barbers found near <strong>{postcode}</strong>.
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: 'rgba(255,255,255,0.55)' }}
                  >
                    Try a different postcode or expand your search area.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ mb: 2, color: '#fff' }}
                  >
                    {barbers.length} barber{barbers.length !== 1 ? 's' : ''}{' '}
                    found near {postcode}
                  </Typography>
                  <Grid container spacing={3}>
                    {barbers.map((barber, idx) => (
                      <Grid item xs={12} sm={6} key={String(barber.id ?? idx)}>
                        <Card
                          elevation={3}
                          sx={{
                            borderRadius: 2,
                            bgcolor: darkCardBg,
                            border: '1px solid rgba(255,255,255,0.08)',
                            transition: 'transform 0.2s',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: '0 8px 20px rgba(221,135,9,0.2)',
                            },
                          }}
                        >
                          <CardActionArea
                            onClick={() =>
                              navigate(
                                `/${
                                  barber.type === 'SHOP' ? 'SHOP' : 'BARBER'
                                }/${barber.id}`,
                                { state: { fromSearch: true } }
                              )
                            }
                          >
                            <CardContent>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  mb: 1.5,
                                }}
                              >
                                <Avatar
                                  src={barber.profileImageSelfLink}
                                  sx={{
                                    bgcolor: accent,
                                    width: 48,
                                    height: 48,
                                  }}
                                >
                                  <ContentCutIcon />
                                </Avatar>
                                <Box>
                                  <Typography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ color: '#fff' }}
                                  >
                                    {barber.name}{' '}
                                  </Typography>
                                </Box>
                              </Box>

                              {barber.addressLineOne && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 0.5,
                                    mb: 0.5,
                                  }}
                                >
                                  <LocationOnIcon
                                    fontSize="small"
                                    sx={{
                                      color: 'rgba(255,255,255,0.5)',
                                      mt: 0.2,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ color: 'rgba(255,255,255,0.6)' }}
                                  >
                                    {barber.addressLineOne}
                                    {barber.addressLineTwo
                                      ? `, ${barber.addressLineTwo}`
                                      : ''}
                                    {barber.city ? `, ${barber.city}` : ''}
                                    {barber.postcode
                                      ? `, ${barber.postcode}`
                                      : ''}
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
            </>
          )}
        </Container>
      </Box>
      <StickyFooter {...companyName} />
    </Box>
  );
};

export default FindBarbers;
